import { useGetUserDetails } from "src/helpers/UserDetails";
import { useGetDialogueById } from "src/views/assistant/AssistantServices";

/**
 * Whether the signed-in user owns this dialogue.
 *
 * Only an owner may share their own chat: a viewer holding someone else's
 * shared link must not be able to re-share it. This reads the same cached
 * `["dialogue", id]` query the chat surfaces already load, so it costs no
 * extra request.
 *
 * `assumeOwnedWhileUnknown` decides the answer before the dialogue has
 * loaded, which the two existing call sites answer differently and both are
 * right for where they sit:
 *
 * - Left false (the default), an unloaded dialogue is not owned. Correct for
 *   a header resuming a chat that already exists, where the owner is a fact
 *   about the server's record and guessing risks offering Share on a chat the
 *   reader is only visiting.
 * - Set true, an unloaded dialogue is owned. Correct where the chat may have
 *   just been created in place: there is no persisted dialogue to read yet,
 *   and the user who is typing into it is its author.
 */
export const useIsDialogueOwner = (
  dialogueId: number | null | undefined,
  {
    assumeOwnedWhileUnknown = false,
  }: { assumeOwnedWhileUnknown?: boolean } = {},
): boolean => {
  const currentUser = useGetUserDetails();
  const { data: dialogue } = useGetDialogueById({
    dialogueId: dialogueId ?? null,
  });

  if (!currentUser) return false;
  if (dialogue?.user_id == null) return assumeOwnedWhileUnknown;
  return dialogue.user_id === currentUser.id;
};

export default useIsDialogueOwner;
